import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { jobs as jobsApi, tokens } from '../utils/api';
import toast from 'react-hot-toast';

const typeColor   = { JOB:'chip-blue', SERVICE:'chip-teal', TASK:'chip-green', FREELANCE:'chip-amber' };
const statusColor = { OPEN:'chip-green', ASSIGNED:'chip-blue', COMPLETED:'chip-teal', CLOSED:'chip-gray', PENDING_VERIFY:'chip-amber' };

export default function JobDetail() {
  const { id }      = useParams();
  const { t, user, fetchBalance } = useApp();
  const navigate     = useNavigate();
  const [job,       setJob]       = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [applying,  setApplying]  = useState(false);
  const [applied,   setApplied]   = useState(false);
  const [showForm,  setShowForm]  = useState(false);
  const [applyForm, setApplyForm] = useState({ message: '', contact_info: '', experience_proof: '' });
  const [contact,   setContact]   = useState(null);

  const [unlocking, setUnlocking] = useState(false);
  const [balance,   setBalance]   = useState(null);

  useEffect(() => {
    jobsApi.get(id)
      .then(r => setJob(r))
      .catch(() => navigate('/jobs'))
      .finally(() => setLoading(false));
    if (user) tokens.balance().then(r => setBalance(r.balance)).catch(() => {});
  }, [id, user, navigate]);

  const handleApply = async e => {
    if (e) e.preventDefault();
    if (!user) { navigate('/login'); return; }
    setApplying(true);
    try {
      await jobsApi.apply(id, applyForm);
      fetchBalance();
      toast.success(t('applied_success'));
      setApplied(true);
      setShowForm(false);
    } catch (err) { toast.error(err.error || t('error_occurred')); }
    finally { setApplying(false); }
  };


  const handleUnlock = async () => {
    if (!user) { navigate('/login'); return; }
    setUnlocking(true);
    try {
      const res = await jobsApi.unlockContact(id);
      setContact({ email: res.posterEmail, phone: res.posterPhone });
      fetchBalance();
      toast.success(res.alreadyUnlocked ? 'Contact already unlocked' : 'Contact unlocked! (−2 tokens)');
    } catch (err) { toast.error(err.error || t('error_occurred')); }
    finally { setUnlocking(false); }
  };

  if (loading) return <div className="page-loader"><div className="spinner" /></div>;
  if (!job)    return null;

  const isOwner  = user?.id === job.posterId;
  const canApply = user?.role === 'SEEKER' && job.status === 'OPEN' && !applied;
  const daysAgo  = Math.floor((Date.now() - new Date(job.createdAt)) / 86400000);

  return (
    <div className="container" style={{ paddingTop: '36px', paddingBottom: '60px', maxWidth: '860px' }}>
      <Link to="/jobs" style={{ fontSize: '13px', color: 'var(--text3)', display: 'inline-flex', alignItems: 'center', gap: '4px', marginBottom: '20px', textDecoration: 'none' }}>
        ← Back to jobs
      </Link>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '20px', alignItems: 'start' }}>
        {/* Main */}
        <div>
          <div className="card" style={{ marginBottom: '14px' }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <span className={`chip ${typeColor[job.type] ?? 'chip-gray'}`}>{job.type}</span>
              <span className={`chip ${statusColor[job.status] ?? 'chip-gray'}`}>{job.status}</span>
              <span className="chip chip-gray">{job.category}</span>
            </div>

            <h1 style={{ fontSize: '22px', fontWeight: 700, lineHeight: 1.3, marginBottom: '10px' }}>{job.title}</h1>

            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '13px', color: 'var(--text3)', marginBottom: '20px' }}>
              <span>📍 {job.location}</span>
              <span>💰 ৳{Number(job.budget).toLocaleString()}</span>
              {job.deadline && <span>📅 Deadline: {new Date(job.deadline).toLocaleDateString('en-BD', { dateStyle: 'medium' })}</span>}
              <span>🕐 Posted {daysAgo === 0 ? 'today' : `${daysAgo}d ago`}</span>
            </div>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
              <h2 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>Description</h2>
              <p style={{ fontSize: '15px', lineHeight: 1.75, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{job.description}</p>
            </div>
          </div>

          {/* Apply */}
          {canApply && (
            <button className="btn btn-primary btn-lg btn-full" onClick={() => setShowForm(true)}>
              {t('apply')} (costs 1 token)
            </button>
          )}


          {applied && (
            <div className="card" style={{ background: 'var(--brand-light)', border: 'none', textAlign: 'center', padding: '24px' }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>✅</div>
              <div style={{ fontWeight: 600, color: 'var(--brand-dark)' }}>Application submitted!</div>
              <div style={{ fontSize: '13px', color: 'var(--text2)', marginTop: '4px' }}>The poster will review your application soon.</div>
            </div>
          )}

          {!user && job.status === 'OPEN' && (
            <div className="card" style={{ textAlign: 'center', padding: '24px' }}>
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>Want to apply?</div>
              <div style={{ fontSize: '13px', color: 'var(--text3)', marginBottom: '14px' }}>Create a seeker account to apply to this job.</div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                <Link to="/register" className="btn btn-primary">{t('nav_register')}</Link>
                <Link to="/login"    className="btn btn-outline">{t('login')}</Link>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div>
          <div className="card" style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>Posted by</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--brand-light)', color: 'var(--brand-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', fontWeight: 700 }}>
                {job.posterName?.[0]?.toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: '14px' }}>{job.posterName}</div>
                <div style={{ fontSize: '12px', color: 'var(--text3)' }}>Job Poster</div>
              </div>
            </div>

            {/* Contact Reveal for Seekers */}
            {user?.role === 'SEEKER' && !isOwner && (
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
                {contact || job.posterEmail ? (
                  <div>
                    <div style={{ fontSize: '13px', marginBottom: '6px' }}>📧 {contact?.email || job.posterEmail}</div>
                    <div style={{ fontSize: '13px' }}>📱 {contact?.phone || job.posterPhone}</div>
                  </div>
                ) : (
                  <button className="btn btn-outline btn-full btn-sm" onClick={handleUnlock} disabled={unlocking}>
                    {unlocking ? <span className="spinner" style={{ width: 14, height: 14 }} /> : `🔒 ${t('view_contact')} (−2 tokens)`}
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="card" style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>Job details</div>
            {[
              ['Type',       job.type],
              ['Category',   job.category],
              ['Location',   job.location],
              ['Budget',     `৳${Number(job.budget).toLocaleString()}`],
              ['Status',     job.status],
              ['Applicants', job.applicantCount ?? '—'],
              ['Posted',     new Date(job.createdAt).toLocaleDateString('en-BD', { dateStyle: 'medium' })],
              ...(job.deadline ? [['Deadline', new Date(job.deadline).toLocaleDateString('en-BD', { dateStyle: 'medium' })]] : []),
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: '13px' }}>
                <span style={{ color: 'var(--text3)' }}>{k}</span>
                <span style={{ fontWeight: 500 }}>{v}</span>
              </div>
            ))}
          </div>

          {isOwner && (
            <div className="card">
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>Manage</div>
              <Link to="/dashboard" className="btn btn-outline btn-full btn-sm">View applicants</Link>
            </div>
          )}

          {user && balance !== null && (
            <div style={{ marginTop: '12px', textAlign: 'center', fontSize: '12px', color: 'var(--text3)' }}>
              Balance: <span className="token-badge" style={{ fontSize: '11px', padding: '2px 8px' }}>🪙 {balance}</span>
            </div>
          )}
        </div>
      </div>
      <style>{`@media(max-width:700px){.container>div[style*="grid"]{grid-template-columns:1fr!important}}`}</style>

      {/* Application Modal */}
      {showForm && (
        <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }} onClick={() => setShowForm(false)}>
          <div style={{ background:'var(--surface)', width:'100%', maxWidth:'500px', borderRadius:'16px', overflow:'hidden', animation:'pop 0.3s ease-out' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding:'20px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div style={{ fontWeight:700, fontSize:'18px' }}>Apply for this Job</div>
              <button className="btn btn-xs" onClick={() => setShowForm(false)}>✕</button>
            </div>
            <div style={{ padding:'20px' }}>
              <div className="form-group">
                <label className="form-label">Application Message *</label>
                <textarea className="form-input" rows={4} required
                  value={applyForm.message}
                  onChange={e => setApplyForm({ ...applyForm, message: e.target.value })}
                  placeholder="Why are you the best fit for this job?" />
              </div>
              <div className="form-group">
                <label className="form-label">Contact Info (Optional)</label>
                <input className="form-input" 
                  value={applyForm.contact_info}
                  onChange={e => setApplyForm({ ...applyForm, contact_info: e.target.value })}
                  placeholder="Mobile no. or alternative email" />
              </div>
              <div className="form-group">
                <label className="form-label">Experience Proof (Optional URL/Text)</label>
                <textarea className="form-input" rows={2}
                  value={applyForm.experience_proof}
                  onChange={e => setApplyForm({ ...applyForm, experience_proof: e.target.value })}
                  placeholder="Link to portfolio, CV or past work examples" />
              </div>
              <div style={{ display:'flex', gap:'10px', marginTop:'20px' }}>
                <button className="btn btn-outline btn-full" onClick={() => setShowForm(false)}>Cancel</button>
                <button className="btn btn-primary btn-full" onClick={handleApply} disabled={applying || !applyForm.message}>
                  {applying ? 'Submitting...' : 'Submit (−1 token)'}
                </button>
              </div>
              <p style={{ fontSize:'11px', color:'var(--text3)', textAlign:'center', marginTop:'12px' }}>
                Applying costs 1 token. You have {balance ?? '...'} tokens.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

